'use client';

import React, { useState, useEffect } from 'react';
import { Navbar, Footer } from '../../components/Layout';
import { RotateCcw, AlertTriangle, CheckCircle, HelpCircle } from 'lucide-react';

export default function ReturnPolicyPage() {
  const [returnPolicyContent, setReturnPolicyContent] = useState<string>('');

  useEffect(() => {
    const authApi = process.env.NEXT_PUBLIC_AUTH_API || 'http://localhost:8080';
    fetch(`${authApi}/api/settings`)
      .then(res => res.json())
      .then(settings => {
        if (settings.return_policy_content) {
          setReturnPolicyContent(settings.return_policy_content);
        }
      })
      .catch(err => console.error('Error fetching return policy settings:', err));
  }, []);

  return (
    <div className="min-h-screen bg-[#F8F9F5] font-body text-text-primary flex flex-col justify-between">
      <Navbar />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12 w-full flex-grow text-left">
        {/* Page Header */}
        <div className="mb-10 border-b border-border/60 pb-6">
          <h1 className="text-4xl font-display font-black text-deep-green">Return & Refund Policy</h1>
          <p className="text-text-muted text-sm font-medium mt-2">
            Read about our return eligibility criteria, request procedures, and timelines for receiving your refunds.
          </p>
        </div>

        <div className="space-y-8">
          {/* Quick Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-2xl border border-border/40 shadow-sm flex flex-col items-center text-center">
              <div className="w-12 h-12 bg-pale-green/30 rounded-full flex items-center justify-center text-mid-green mb-4">
                <RotateCcw className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-deep-green text-base mb-1">Return Window</h3>
              <p className="text-xs text-text-muted">Initiate return request within 48 hours for eligible organic items.</p>
            </div>
            
            <div className="bg-white p-6 rounded-2xl border border-border/40 shadow-sm flex flex-col items-center text-center">
              <div className="w-12 h-12 bg-pale-green/30 rounded-full flex items-center justify-center text-mid-green mb-4">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-deep-green text-base mb-1">Perishables Info</h3>
              <p className="text-xs text-text-muted">Fresh meats and milk are non-returnable but eligible for refund if quality is compromised.</p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-border/40 shadow-sm flex flex-col items-center text-center">
              <div className="w-12 h-12 bg-pale-green/30 rounded-full flex items-center justify-center text-mid-green mb-4">
                <CheckCircle className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-deep-green text-base mb-1">Refund Timeline</h3>
              <p className="text-xs text-text-muted">Approved refunds are processed to your original payment method in 3-5 days.</p>
            </div>
          </div>

          {/* Detailed Policy Card */}
          <div className="bg-white p-6 sm:p-8 rounded-3xl border border-border/40 shadow-sm space-y-6">
            {returnPolicyContent ? (
              <div className="text-sm text-text-muted leading-relaxed whitespace-pre-line">
                {returnPolicyContent}
              </div>
            ) : (
              <>
                <section className="space-y-3">
                  <h2 className="text-xl font-bold text-deep-green flex items-center gap-2">
                    <RotateCcw className="w-5 h-5 text-mid-green" /> How to Request a Return
                  </h2>
                  <p className="text-sm text-text-muted leading-relaxed font-medium">
                    To request a return for a delivered order:
                  </p>
                  <ol className="list-decimal list-inside text-sm text-text-muted space-y-1.5 pl-2">
                    <li>Log in to your profile on the storefront.</li>
                    <li>Go to the <span className="font-bold text-deep-green">My Orders</span> page.</li>
                    <li>Expand the delivered order you wish to return.</li>
                    <li>Click the <span className="font-bold text-amber">Request Return</span> action button.</li>
                    <li>Our farm quality control team will evaluate the request and send representatives for inspection.</li>
                  </ol>
                </section>

                <section className="space-y-3">
                  <h2 className="text-xl font-bold text-deep-green flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-mid-green" /> Eligibility Criteria by Product Type
                  </h2>
                  <p className="text-sm text-text-muted leading-relaxed">
                    Organic products are highly sensitive to temperature and environment:
                  </p>
                  <ul className="list-disc list-inside text-sm text-text-muted space-y-1.5 pl-2">
                    <li><span className="font-bold text-deep-green">Spices, Oils & Dry Groceries:</span> Eligible for return if unopened and in original packaging.</li>
                    <li><span className="font-bold text-deep-green">Fresh Produce & Perishables:</span> Generally non-returnable due to hygiene and food safety rules. However, if they are received damaged or rotten, we will issue a complete refund or replacement immediately.</li>
                    <li><span className="font-bold text-deep-green">Meat Cuts:</span> Custom cut non-veg meat items cannot be returned. Please inspect them at the time of delivery.</li>
                  </ul>
                </section>

                <section className="space-y-3">
                  <h2 className="text-xl font-bold text-deep-green flex items-center gap-2">
                    <HelpCircle className="w-5 h-5 text-mid-green" /> Stock Replenishment
                  </h2>
                  <p className="text-sm text-text-muted leading-relaxed">
                    When a return request is officially authorized and processed by our admin panel, the corresponding returned quantities are automatically recalculated and replenished back into our inventory systems, preserving accurate stocks for other buyers.
                  </p>
                </section>
              </>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
